import { Link, useLocation } from "react-router-dom";
import { Mail, AlertTriangle } from "lucide-react";
import { COMPANY, CONTACT_EMAIL } from "@/lib/api";

const COLS = [
  { title: "Explore", links: [["/search", "Search HOAs"], ["/map", "Map View"], ["/states", "Browse by State"], ["/compare", "Compare Communities"], ["/fee-trends", "HOA Fee Trends"]] },
  { title: "Resources", links: [["/articles", "Articles & Guides"], ["/submit-hoa", "Submit an HOA"], ["/contact", "Contact Us"], ["/sitemap", "HTML Sitemap"]] },
  { title: "Legal", links: [["/privacy-policy", "Privacy Policy"], ["/terms-of-service", "Terms of Service"], ["/disclaimer", "Disclaimer"], ["/cookie-policy", "Cookie Policy"], ["/accessibility", "Accessibility Statement"]] },
];
const MOBILE_KEY = [["/search", "Search"], ["/map", "Map"], ["/states", "States"], ["/articles", "Articles"], ["/contact", "Contact"]];
const MOBILE_LEGAL = [["/privacy-policy", "Privacy"], ["/terms-of-service", "Terms"], ["/disclaimer", "Disclaimer"], ["/cookie-policy", "Cookies"], ["/accessibility", "Accessibility"], ["/sitemap", "Sitemap"]];

const isListing = (path) => path.split("/").filter(Boolean).length === 3;

const Credit = () => (
  <>Website Designed by <a href="https://bp.agency" target="_blank" rel="noopener noreferrer" className="text-[#63D8C4] hover:text-white underline" data-testid="footer-designer-link">Brand Partners</a></>
);

const MobileFooter = () => (
  <div className="sm:hidden px-4 pt-7 pb-24 space-y-4" data-testid="footer-mobile">
    <p className="font-display text-lg font-bold text-white">Explore HOA's</p>
    <nav aria-label="Footer" className="flex flex-wrap gap-x-4 gap-y-2 text-sm font-medium text-slate-200">
      {MOBILE_KEY.map(([to, label]) => <Link key={to} to={to} className="hover:text-[#63D8C4]" data-testid={`footer-mobile-link-${to.slice(1)}`}>{label}</Link>)}
    </nav>
    <nav aria-label="Legal" className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-slate-400">
      {MOBILE_LEGAL.map(([to, label]) => <Link key={to} to={to} className="hover:text-white" data-testid={`footer-mobile-legal-${to.slice(1)}`}>{label}</Link>)}
    </nav>
    <p className="pt-3 border-t border-white/10 text-[11px] leading-relaxed text-slate-400">© {new Date().getFullYear()} {COMPANY}. All rights reserved. <Credit /></p>
  </div>
);

export const Footer = () => {
  const { pathname } = useLocation();
  return (
  <footer className="bg-[#061330] text-slate-300 mt-12 sm:mt-24" data-testid="site-footer">
    {!isListing(pathname) && (
      <div className="bg-[#243B5E]" data-testid="footer-reliability-notice">
        <p className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-center gap-2 sm:gap-3 text-center text-xs sm:text-base text-[#F5D06B]">
          <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" aria-hidden />
          Information is deemed reliable but not guaranteed. Verify with the HOA directly.
        </p>
      </div>
    )}
    <MobileFooter />
    <div className="hidden sm:grid max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 gap-12 sm:grid-cols-3 lg:grid-cols-5">
      <div className="sm:col-span-3 lg:col-span-2 space-y-5">
        <p className="font-display text-xl font-bold text-white">Explore HOA's</p>
        <p className="text-sm leading-relaxed max-w-sm">A nationwide directory of homeowners association fees, pet rules, rental restrictions and amenities, so you know the rules before you move in.</p>
        <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2 text-sm text-[#63D8C4] hover:text-white" data-testid="footer-email-link">
          <Mail className="h-4 w-4" aria-hidden /> {CONTACT_EMAIL}
        </a>
      </div>
      {COLS.map((c) => (
        <div key={c.title}>
          <h2 className="text-white font-semibold mb-4 text-base">{c.title}</h2>
          <ul className="space-y-2.5">
            {c.links.map(([to, label]) => (
              <li key={to}><Link to={to} className="text-sm hover:text-[#63D8C4] transition-colors" data-testid={`footer-link-${to.slice(1)}`}>{label}</Link></li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <div className="hidden sm:block border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-row gap-3 justify-between text-xs text-slate-400">
        <p>© {new Date().getFullYear()} {COMPANY}. All rights reserved. <Credit /></p>
        <p>Information is provided for reference only. Always confirm with the association.</p>
      </div>
    </div>
  </footer>
  );
};
